import { Alert, Box, Stack } from "@mui/material";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { DateStepper } from "components/DateStepper";
import { EnsureGcsTokenExists } from "components/scrapEyeVideoPlayer/GcsTokenProvider";
import { StrikeRulesBox } from "components/strategyDashboard/common/StrikeRulesBox";
import { useDayNavigation } from "components/strategyDashboard/heats/context/DayNavigationProvider";
import { useHeatsData } from "components/strategyDashboard/heats/context/HeatsDataContext";
import { useHeatsRowFilter } from "components/strategyDashboard/heats/context/HeatsRowFilterContext";
import { HeatsTabProviders } from "components/strategyDashboard/heats/context/HeatsTabProviders";
import { useHeatsTimeRange } from "components/strategyDashboard/heats/context/HeatsTimeRangeContext";
import { ComplianceLegendBox } from "components/strategyDashboard/heats/ComplianceLegendBox";
import { useTenantTranslation } from "hooks/formatters";
import { useIsAdmin, useTenant } from "hooks/settings";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";
import { ChemistryElementProvider } from "./chemistryTab/ChemistryElementContext";
import HeatDetailsDrawer from "./HeatDetailsDrawer";
import { HeatsSummary } from "./HeatsSummary";
import { HeatsTable } from "./HeatsTable";
import { IgnoreMaterialsToggle } from "./IgnoreMaterialsToggle";
import { ShiftStepper } from "./ShiftStepper";

const HeatsTabContent = () => {
  const isAdmin = useIsAdmin();
  const { t } = useTenantTranslation();
  const { isError, scrapEyeOnly, hasPriceComparison, data } = useHeatsData();
  const { viewMode } = useHeatsTimeRange();
  const { isFiltering } = useHeatsRowFilter();

  const strikeRules = data?.strike_rules;

  return (
    <Stack
      gap={1}
      sx={{
        maxHeight: "100%",
        maxWidth: 1920,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        rowGap={2}
        alignItems="center"
        justifyContent="space-between"
        mt={2}
        flexWrap="wrap"
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          {viewMode === "shift" ? <ShiftStepper /> : <HeatsTabDateStepper />}
          {isAdmin ? <HeatsExcelButton /> : null}
          <Box pb={3}>
            <IgnoreMaterialsToggle />
          </Box>
        </Stack>
        {isFiltering ? null : <HeatsSummary />}
      </Stack>
      {isError ? <Alert severity="error">{t("errorLoadingData")}</Alert> : null}
      <HeatsTable />
      {hasPriceComparison && !scrapEyeOnly ? <ComplianceLegendBox /> : null}
      {scrapEyeOnly && strikeRules !== undefined && strikeRules.length > 0 ? (
        <StrikeRulesBox strikeRules={strikeRules} />
      ) : null}
      <HeatDetailsDrawer />
    </Stack>
  );
};

const HeatsTabWithChemistryProvider = () => {
  const { availableElements } = useHeatsData();
  return (
    <ChemistryElementProvider availableElements={availableElements}>
      <HeatsTabContent />
    </ChemistryElementProvider>
  );
};

export const HeatsTab = () => {
  return (
    <HeatsTabProviders>
      <EnsureGcsTokenExists>
        <HeatsTabWithChemistryProvider />
      </EnsureGcsTokenExists>
    </HeatsTabProviders>
  );
};

const HeatsTabDateStepper = () => {
  const dayNav = useDayNavigation();
  if (!dayNav) return null;
  const { date, setDate } = dayNav;
  return (
    <Stack direction="row" justifyContent="center">
      <DateStepper date={date} setDate={setDate} />
    </Stack>
  );
};

const HeatsExcelButton = () => {
  const { timeRange } = useHeatsTimeRange();
  const tenantName = useTenant();
  const [trigger] =
    backendApi.endpoints.getStrategyDashboardHeats.useLazyQuery();
  const getBlob = () =>
    downloadAsExcel(trigger, {
      tenantName,
      start: timeRange.start,
      end: timeRange.end,
      page: 0,
      resultsPerPage: undefined,
    });
  return <ExcelDownloadButton variant="outlined" getBlob={getBlob} />;
};
